import { useEffect, useState } from 'react';
import './TreeComponent.css';
import { useCountriesTreeList } from 'entities/geo/countries/countries-list.hook';

interface City {
  id: number;
  name: string;
}

interface Region {
  id: number;
  name: string;
  cities: City[];
}

interface Country {
  id: number;
  name: string;
  regions: Region[];
}

interface SelectedItems {
  regions: Region[];
  cities: City[];
}

interface TreeComponentProps {
  onDataChange?: (data: { regions: any[]; cities: any[] }) => void;
}

const TreeComponent = ({ onDataChange }: TreeComponentProps) => {
  const { isLoading, error, list: data } = useCountriesTreeList();

  const [expandedCountries, setExpandedCountries] = useState<Record<number, boolean>>({});
  const [expandedRegions, setExpandedRegions] = useState<Record<number, boolean>>({});
  const [selectedItems, setSelectedItems] = useState<SelectedItems>({
    regions: [],
    cities: []
  });

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!isInitialized && data.length > 0) {
      setIsInitialized(true);
    }
  }, [data, isInitialized]);

  useEffect(() => {
    if (isInitialized && onDataChange) {
      onDataChange(selectedItems);
    }
  }, [selectedItems, onDataChange, isInitialized]);
  
  const toggleCountry = (countryId: number) => {
    setExpandedCountries(prev => ({
      ...prev,
      [countryId]: !prev[countryId]
    }));
  };

  const toggleRegion = (regionId: number) => {
    setExpandedRegions(prev => ({
      ...prev,
      [regionId]: !prev[regionId]
    }));
  };

  const handleCountrySelect = (country: Country) => {
    setSelectedItems(prev => {
      const isSelected = country.regions.length > 0 
        ? country.regions.every(region => 
          prev.regions.some(r => r.id === region.id)
        )
        : false;

      if (isSelected) {
        const countryRegionIds = country.regions.map(r => r.id);
        const countryCityIds = country.regions.flatMap(r => r.cities.map(c => c.id));
        
        const updatedRegions = prev.regions.filter(r => !countryRegionIds.includes(r.id));
        const updatedCities = prev.cities.filter(c => !countryCityIds.includes(c.id));
        
        return {
          regions: updatedRegions,
          cities: updatedCities
        };
      } else {
        return {
          regions: [...prev.regions, ...country.regions],
          cities: [...prev.cities, ...country.regions.flatMap(r => r.cities)]
        };
      }
    });
  };

  const handleRegionSelect = (region: Region) => {
    setSelectedItems(prev => {
      const isSelected = prev.regions.some(r => r.id === region.id);
      
      if (isSelected) {
        const updatedRegions = prev.regions.filter(r => r.id !== region.id);
        const updatedCities = prev.cities.filter(city => 
          !region.cities.some(regionCity => regionCity.id === city.id)
        );
        
        return {
          regions: updatedRegions,
          cities: updatedCities
        };
      } else {
        return {
          regions: [...prev.regions, region],
          cities: [...prev.cities, ...region.cities]
        };
      }
    });
  };

  const handleCitySelect = (city: City, region: Region) => {
    setSelectedItems(prev => {
      const isSelected = prev.cities.some(c => c.id === city.id);
      
      if (isSelected) {
        const updatedCities = prev.cities.filter(c => c.id !== city.id);
        
        const regionCitiesInSelection = updatedCities.filter(c => 
          region.cities.some(rc => rc.id === c.id)
        );
        
        let updatedRegions = prev.regions;
        if (regionCitiesInSelection.length === 0) {
          updatedRegions = prev.regions.filter(r => r.id !== region.id);
        }
        
        return {
          regions: updatedRegions,
          cities: updatedCities
        };
      } else {
        const updatedCities = [...prev.cities, city];
        
        const isRegionSelected = prev.regions.some(r => r.id === region.id);
        let updatedRegions = prev.regions;
        if (!isRegionSelected) {
          updatedRegions = [...prev.regions, region];
        }
        
        return {
          regions: updatedRegions,
          cities: updatedCities
        };
      }
    });
  };

  const isCountrySelected = (country: Country) => {
    if (country.regions.length === 0) return false;
    return country.regions.every(region => 
      selectedItems.regions.some(r => r.id === region.id)
    );
  };

  const isCountryPartial = (country: Country) => {
    if (country.regions.length === 0) return false;
    const selectedRegionsInCountry = selectedItems.regions.filter(region => 
      country.regions.some(cr => cr.id === region.id)
    );
    return selectedRegionsInCountry.length > 0 && 
           selectedRegionsInCountry.length < country.regions.length;
  };

  const isRegionSelected = (region: Region) => {
    return selectedItems.regions.some(r => r.id === region.id);
  };

  const isRegionPartial = (region: Region) => {
    const selectedCitiesInRegion = selectedItems.cities.filter(city => 
      region.cities.some(rc => rc.id === city.id)
    );
    return selectedCitiesInRegion.length > 0 && 
           selectedCitiesInRegion.length < region.cities.length;
  };

  const isCitySelected = (city: City) => {
    return selectedItems.cities.some(c => c.id === city.id);
  };

  if (isLoading) {
    return <div>Загрузка...</div>;
  }

  if (error) {
    return <div>Ошибка загрузки данных</div>;
  }

  return (
    <div className="tree-container">
      <div className="tree-section">
        <h3>Дерево выбора</h3>
        <div className="tree">
          {data.map((country: Country) => (
            <div key={country.id} className="country-item">
              <div className="country-header">
                {country.regions.length > 0 && (
                  <span 
                    className="toggle-icon"
                    onClick={() => toggleCountry(country.id)}
                  >
                    {expandedCountries[country.id] ? '▼' : '►'}
                  </span>
                )}
                {country.regions.length === 0 && (
                  <span className="toggle-icon" style={{ visibility: 'hidden' }}>►</span>
                )}
                <input
                  type="checkbox"
                  checked={isCountrySelected(country)}
                  onChange={() => handleCountrySelect(country)}
                  className={isCountryPartial(country) ? 'partial' : ''}
                  disabled={country.regions.length === 0}
                />
                <span 
                  className="country-name"
                  onClick={() => country.regions.length > 0 && toggleCountry(country.id)}
                  style={{ cursor: country.regions.length > 0 ? 'pointer' : 'default' }}
                >
                  {country.name}
                  {country.regions.length === 0 && ' (нет регионов)'}
                </span>
              </div>
              
              {expandedCountries[country.id] && country.regions.length > 0 && (
                <div className="regions-list">
                  {country.regions.map((region: Region) => (
                    <div key={region.id} className="region-item">
                      <div className="region-header">
                        <span 
                          className="toggle-icon"
                          onClick={() => toggleRegion(region.id)}
                        >
                          {expandedRegions[region.id] ? '▼' : '►'}
                        </span>
                        <input
                          type="checkbox"
                          checked={isRegionSelected(region)}
                          onChange={() => handleRegionSelect(region)}
                          className={isRegionPartial(region) ? 'partial' : ''}
                        />
                        <span 
                          className="region-name"
                          onClick={() => toggleRegion(region.id)}
                        >
                          {region.name}
                        </span>
                      </div>
                      
                      {expandedRegions[region.id] && (
                        <div className="cities-list">
                          {region.cities.map((city: City) => (
                            <div key={city.id} className="city-item">
                              <input
                                type="checkbox"
                                checked={isCitySelected(city)}
                                onChange={() => handleCitySelect(city, region)}
                              />
                              <span>{city.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="selected-section">
        <h3>Выбранные элементы</h3>
        <div className="selected-items">
          <div className="selected-regions">
            <h4>Регионы ({selectedItems.regions.length}):</h4>
            {selectedItems.regions.map(region => (
              <div key={region.id} className="selected-item">
                {region.name}
              </div>
            ))}
          </div>
          <div className="selected-cities">
            <h4>Города ({selectedItems.cities.length}):</h4>
            {selectedItems.cities.map(city => (
              <div key={city.id} className="selected-item">
                {city.name}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TreeComponent;
